/**
 * THE NARROW-VIEWPORT SIGNAL for the call-centre workspace.
 *
 * WHY IT EXISTS (controller item 3, measured on the running page inside the
 * REAL shell): at 834 the team queue's rows measured 145-167px tall against
 * 124px at 1440, because seven columns of content are being folded into a
 * width that cannot hold them. Four rows filled roughly 640px of a 1112px
 * tablet screen, so a manager triaging work saw fewer than a screenful.
 *
 * WHAT IT DOES NOT DO: it does not hide a control. The column it drops carries
 * INFORMATION -- the last-activity note -- and every action, count and owner
 * state stays on the row at every width. The craft law's fence is about
 * removing working controls to reach a number, and nothing here is removed to
 * reach a number.
 *
 * Lane 3 built the same signal for its own register
 * (`frontend/src/dashboard/coc/use-narrow-viewport.ts`). That file is lane
 * 3's, so this lane does not import it; the shape is the same and the
 * breakpoint is the one dashboard.css already folds the shell at, rather than
 * a new one minted here.
 *
 * SSR-safe: no `window` during a server render, so the desktop column set is
 * what a static render produces.
 */
import { useEffect, useState } from 'react';

/** The shell's own fold, not a new breakpoint (dashboard.css TABLET-SHELL-V1). */
export const WORKSPACE_NARROW_QUERY = '(max-width: 900px)';

export function useNarrowWorkspace(): boolean {
  const [narrow, setNarrow] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(WORKSPACE_NARROW_QUERY);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return narrow;
}
